import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useAuthStore } from './auth';

export const useDevModeStore = defineStore('devMode', () => {
  // Load from localStorage or default to disabled
  const isDevModeEnabled = ref(localStorage.getItem('stw-dev-mode') === 'true');

  // Get auth store for user info
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  // Environment detection
  const currentMode = computed(() => import.meta.env.MODE);

  // Should show dev mode toggle button
  const shouldShowDevModeToggle = computed(() => {
    const mode = currentMode.value;

    // Local development: always show
    if (mode === 'development') {
      return true;
    }

    // Production: show only to admins
    if (mode === 'production') {
      return user.value && user.value?.is_admin;
    }

    // Staging: always show
    if (mode === 'staging') {
      return true;
    }

    // Default: never show
    return false;
  });

  // Can access dev tools (similar logic to backend generalDevAuth)
  const canAccessDevTools = computed(() => {
    const mode = currentMode.value;

    // Local development: allow all users
    if (mode === 'development') {
      return true;
    }

    // Production: admin-only
    if (mode === 'production') {
      return user.value && user.value?.is_admin;
    }

    // Staging: allow all users
    if (mode === 'staging') {
      return true;
    }

    // Default: require admin
    return user.value && user.value?.is_admin;
  });

  // Should show admin test components
  const shouldShowAdminTestComponents = computed(() => {
    const mode = currentMode.value;

    // Production: never show
    if (mode === 'production') {
      return false;
    }

    // Local development: show only if dev mode is enabled
    if (mode === 'development') {
      return isDevModeEnabled.value;
    }

    // Staging: show only to admins AND if dev mode is enabled
    if (mode === 'staging') {
      return user.value && user.value?.is_admin && isDevModeEnabled.value;
    }

    // Default: never show
    return false;
  });

  // Actions
  const toggleDevMode = () => {
    isDevModeEnabled.value = !isDevModeEnabled.value;
    localStorage.setItem('stw-dev-mode', isDevModeEnabled.value.toString());
  };

  const enableDevMode = () => {
    isDevModeEnabled.value = true;
    localStorage.setItem('stw-dev-mode', 'true');
  };

  const disableDevMode = () => {
    isDevModeEnabled.value = false;
    localStorage.setItem('stw-dev-mode', 'false');
  };

  return {
    // State
    isDevModeEnabled,
    currentMode,

    // Computed logic
    shouldShowDevModeToggle,
    shouldShowAdminTestComponents,
    canAccessDevTools,

    // Actions
    toggleDevMode,
    enableDevMode,
    disableDevMode,
  };
});
